import { CopyLinkField } from '@/components/ui/clipboard/CopyLinkField';
import { useInvitationData, useInvitationStoreSelector } from '@/contexts/invitation-context';
import type { InvitationChannelMode } from '@/stores/invitation-store';
import { Button, Text, VStack } from '@chakra-ui/react';
import { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import BulkLinkDeliveryView from './bulk-link-delivery-view';
import BulkWishlistDetailsView from './bulk-wishlist-details-view';
import { BulkEmailNote, partitionByEmail } from './components';
import { DmCommentDetailsView } from './dm-comment-details-view';
import { InvitationDeliveryBanner } from './invitation-delivery-banner';
import ProposeCollabEmailStep from './propose-collab-email-step';

interface InvitationDetailsViewProps {
  onClose: ({ success }: { success?: boolean }) => void;
}

/**
 * Link mode of the mono delivery step: the shareable link is the whole
 * delivery. A prominent copy field, one explanatory clause, and "Done" — which
 * closes on success (the invitation already exists, it was created upfront).
 */
const LinkDeliveryView: React.FC<InvitationDetailsViewProps> = ({ onClose }) => {
  const { t } = useTranslation();
  const { invitationData } = useInvitationData();

  return (
    <VStack align="stretch" w="full" gap={4}>
      {invitationData?.invitationLink && (
        <CopyLinkField
          value={invitationData.invitationLink}
          copyLabel={t('invitations.modal.collab.wizard.done.copy')}
        />
      )}
      <Text fontSize="sm" color="fg.muted" lineHeight="1.5">
        {t('invitations.modal.channel.link.explainer')}
      </Text>
      <Button w="full" colorPalette="primary" onClick={() => onClose({ success: true })}>
        {t('invitations.modal.channel.link.done')}
      </Button>
    </VStack>
  );
};

/**
 * Mono delivery step: a shared recipient banner (with "Change" back to the
 * channel step) heads all four modes, then the body is routed by the channel
 * mode chosen upstream. A null/stale mode falls back to the link.
 */
const MonoDeliveryView: React.FC<InvitationDetailsViewProps> = ({ onClose }) => {
  const invitationChannelMode = useInvitationStoreSelector((state) => state.invitationChannelMode);
  const setCurrentView = useInvitationStoreSelector((state) => state.setCurrentView);
  const mode: InvitationChannelMode = invitationChannelMode ?? 'link';

  return (
    <VStack align="stretch" w="full" gap={4}>
      <InvitationDeliveryBanner
        mode={mode}
        onChangeChannel={() => setCurrentView('invitationChannel')}
      />
      {mode === 'email' && <ProposeCollabEmailStep onClose={onClose} />}
      {(mode === 'dm' || mode === 'comment') && (
        <DmCommentDetailsView onClose={onClose} mode={mode} />
      )}
      {mode === 'link' && <LinkDeliveryView onClose={onClose} />}
    </VStack>
  );
};

/**
 * Bulk wishlist delivery step, routed by the channel mode picked upstream: a
 * shared recipient-count banner (with "Change" back to the channel step), then
 * the body is the outreach compose (email) or the short create-invitations
 * surface (link). Email mode counts (and warns about) the items WITH an email;
 * link mode covers the whole selection — mail-less creators are anchored on
 * their social profile server-side.
 */
const BulkDeliveryView: React.FC<InvitationDetailsViewProps> = ({ onClose }) => {
  const { target } = useInvitationData();
  const invitationChannelMode = useInvitationStoreSelector((state) => state.invitationChannelMode);
  const setCurrentView = useInvitationStoreSelector((state) => state.setCurrentView);

  const items = target?.type === 'wishlist-bulk' ? target.data : [];
  const partition = useMemo(() => partitionByEmail(items), [items]);
  const mode = invitationChannelMode === 'email' ? 'email' : 'link';
  const isEmailMode = mode === 'email';

  return (
    <VStack align="stretch" w="full" gap={4}>
      <InvitationDeliveryBanner
        mode={mode}
        count={isEmailMode ? partition.emailCount : items.length}
        onChangeChannel={() => setCurrentView('invitationChannel')}
      />
      {isEmailMode ? (
        <>
          <BulkEmailNote partition={partition} />
          <BulkWishlistDetailsView onClose={onClose} />
        </>
      ) : (
        <BulkLinkDeliveryView onClose={onClose} totalCount={items.length} />
      )}
    </VStack>
  );
};

/**
 * Invitation details step, routed by target kind: the bulk wishlist branch
 * routes by the channel mode (email compose vs. link creation); every mono
 * target goes through the channel-mode-routed delivery view.
 */
const InvitationDetailsView: React.FC<InvitationDetailsViewProps> = memo(({ onClose }) => {
  const { invitationType } = useInvitationData();

  if (invitationType === 'wishlist-bulk') {
    return <BulkDeliveryView onClose={onClose} />;
  }
  return <MonoDeliveryView onClose={onClose} />;
});

InvitationDetailsView.displayName = 'InvitationDetailsView';

export default InvitationDetailsView;
